import { createEffect, ofType, Actions } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import type {
  CreateLeadBoilerSchema,
  LeadProductSchema,
} from '@integration/api-client/feh';
import { Store } from '@ngrx/store';
import { getAppConfig } from '@core/config-store';
import { concatLatestFrom } from '@ngrx/operators';
import type { ClientEnvironmentConfig } from '@core/config-models';
import { LocationService } from '@core/jss-seo';
import {
  postCreateLeadBoiler,
  postCreateLeadBoilerError,
  postCreateLeadBoilerSuccess,
} from '@innogy/feh/temporary-core-modules';

import * as BoilersActions from './boilers.actions';
import { selectBoilersAdviceFormSubmitRedirect } from './boilers.selectors';
import type { BoilersAdviceFormData } from '../models/boilers-advice-form-data.interface';

const adviceFormKey = 'boilers-advice-form';

@Injectable()
export class BoilersEffects {
  readonly currentDate = new Date();

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly location: LocationService
  ) {}

  handleSubmitAdviceForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BoilersActions.submitAdviceForm),
      concatLatestFrom(() => [this.store.select(getAppConfig)]),
      map(([params, config]) => {
        return postCreateLeadBoiler({
          actionId: adviceFormKey,
          payload: this.mapFormToApiPayload(params.formData, config),
        });
      })
    )
  );

  handleCreateLeadError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postCreateLeadBoilerError),
      filter((params) => params.actionId === adviceFormKey),
      map(() => BoilersActions.submitAdviceFormError())
    )
  );

  handleCreateLeadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postCreateLeadBoilerSuccess),
      filter((params) => params.actionId === adviceFormKey),
      concatLatestFrom(() => [
        this.store.select(selectBoilersAdviceFormSubmitRedirect),
      ]),
      tap(([_, redirectUrl]) =>
        redirectUrl ? this.location.navigateScLink(redirectUrl) : undefined
      ),
      map(() => BoilersActions.submitAdviceFormSuccess())
    )
  );

  private mapFormToApiPayload(
    formData: BoilersAdviceFormData,
    config: ClientEnvironmentConfig | null
  ): CreateLeadBoilerSchema {
    return {
      type: 'boiler',
      lead: {
        title: formData.salutation,
        initials: formData.initials,
        prefix: formData.insertion,
        lastName: formData.lastName,
        addresses: {
          communication: {
            postcode: formData.address.postcode,
            street: formData.address.street,
            houseNumber: formData.address.houseNumber,
            houseNumberExtension:
              formData.address.houseNumberAddition.trim() || undefined,
            city: formData.address.city,
          },
        },
        phone: formData.phone,
        email: formData.email,
      },
      consent: {
        dataUsage: formData.consentContact,
        promotion: formData.consentPromotion,
      },
      context: {
        channel: 'Online',
        requestType: 'Advies',
        brand: config?.brand === 'ed' ? 'energiedirect' : 'essent',
        leadContractType: formData.leadContractType as any,
      },
      metadata: {
        temperature: 4,
        createdOn: this.currentDate.toISOString(),
        source: 'Online',
        environment: 'Consument',
        leadTrackingId: 'DirectOther',
        e2eTrackingId: '',
        remarks: formData.remarks || undefined,
      },
      products: this.mapBoilerProducts(formData.products),
    };
  }

  private mapBoilerProducts(products: string[] = ['']): LeadProductSchema[] {
    const boilerData = { category: 'boiler', description: 'boiler' };

    if (products.length) {
      return products.map((product) => ({
        ...boilerData,
        subCategory: product,
      }));
    }
    return [boilerData];
  }
}
